// ─── Layer Order Controls ─────────────────────────────────────
// Compact 4-button control for stacking order on a selected element.
// Shown at the top of the Element Style panel.

import React from 'react';
import { ChevronsUp, ChevronUp, ChevronDown, ChevronsDown, Layers } from 'lucide-react';

const Z_MIN = 1;
const Z_MAX = 20;

function getZ(el) {
  return el?.layout?.mobile?.z ?? el?.layout?.desktop?.z ?? 1;
}

export default function LayerOrderControls({
  sectionId,
  element,
  allElements,
  onLayerUpdate,
}) {
  if (!element || !onLayerUpdate) return null;

  const currentZ = getZ(element);
  const others = (allElements || []).filter(e => e.id !== element.id);
  const maxZ = others.length > 0 ? Math.max(...others.map(getZ)) : currentZ;
  const minZ = others.length > 0 ? Math.min(...others.map(getZ)) : currentZ;

  const set = (z) => {
    const clamped = Math.min(Z_MAX, Math.max(Z_MIN, z));
    onLayerUpdate(sectionId, element.id, 'z', clamped);
  };

  const isFront = currentZ >= maxZ && others.length > 0;
  const isBack = currentZ <= minZ && others.length > 0;

  const Btn = ({ onClick, disabled, label, testId, children }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      data-testid={testId}
      className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-md text-[10px] font-medium text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600 dark:hover:bg-blue-900/20 dark:hover:border-blue-700 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-gray-50 disabled:hover:border-gray-200 disabled:hover:text-gray-600 transition-colors"
    >
      {children}
    </button>
  );

  return (
    <div
      className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden"
      data-testid="layer-order-controls"
    >
      <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 flex items-center justify-between">
        <h3 className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
          <Layers className="w-3 h-3" /> Layer
        </h3>
        <span className="text-[9px] text-gray-400 dark:text-gray-500">z:{currentZ}</span>
      </div>
      <div className="p-2 flex items-center gap-1">
        <Btn
          onClick={() => set(Math.max(...[...others.map(getZ), currentZ]) + 1)}
          disabled={isFront}
          label="Bring to Front"
          testId="layer-to-front"
        >
          <ChevronsUp className="w-3.5 h-3.5" />
        </Btn>
        <Btn
          onClick={() => set(currentZ + 1)}
          disabled={currentZ >= Z_MAX}
          label="Bring Forward"
          testId="layer-forward"
        >
          <ChevronUp className="w-3.5 h-3.5" />
        </Btn>
        <Btn
          onClick={() => set(currentZ - 1)}
          disabled={currentZ <= Z_MIN}
          label="Send Backward"
          testId="layer-backward"
        >
          <ChevronDown className="w-3.5 h-3.5" />
        </Btn>
        <Btn
          onClick={() => set(Math.max(Z_MIN, Math.min(...[...others.map(getZ), currentZ]) - 1))}
          disabled={isBack}
          label="Send to Back"
          testId="layer-to-back"
        >
          <ChevronsDown className="w-3.5 h-3.5" />
        </Btn>
      </div>
    </div>
  );
}
